import React, { useState } from 'react';
import {
  accountFetchRequest,
  adminGetUsers,
  fillUserAddresses,
  adminReviewAction,
  adminUpdateGender,
  adminUpdateLocation,
  callableErrorMessage,
  confirmAction,
  copyText,
  deleteAccount,
  enableUser,
  userLocationLines,
  userPhotoCountLabel,
  userPhotoList
} from '../../../adminApi';

function LookupPanel(props) {
  const [query, setQuery] = useState('');
  const [quick, setQuick] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const selected = users.find((user) => user.userId === selectedId);
  const photo = userPhotoList(selected)[0];

  const search = (event) => {
    if (event) {
      event.preventDefault();
    }
    const value = query.trim();
    if (!value) {
      setError('Enter an id, email, phone, or name.');
      return;
    }
    setBusy(true);
    setError('');
    accountFetchRequest(value, quick)
      .then((ids) => adminGetUsers(ids || []))
      .then((loaded) => {
        const next = loaded || [];
        setUsers(next);
        setSelectedId(next[0] ? next[0].userId : '');
        fillUserAddresses(next, setUsers, props.onUsersUpdated);
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const run = (label, work) => {
    if (!selected) {
      return;
    }
    if (!confirmAction(label)) {
      return;
    }
    setBusy(true);
    setError('');
    work()
      .then(() => {
        if (props.onChanged) {
          props.onChanged();
        }
        return adminGetUsers([selected.userId]);
      })
      .then((loaded) => {
        const nextUser = (loaded || [])[0];
        if (!nextUser) {
          setUsers((current) => current.filter((user) => user.userId !== selected.userId));
          setSelectedId('');
          return;
        }
        setUsers((current) => current.map((user) => (user.userId === nextUser.userId ? nextUser : user)));
        fillUserAddresses([nextUser], setUsers, props.onUsersUpdated);
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  return (
    <section>
      <div className="admin-section-head">
        <div>
          <h2>Look up user</h2>
          <p className="admin-muted">
            {quick ? 'Quick search: id, email, or number' : 'Full search also matches names'}
          </p>
        </div>
        <label className="admin-choice">
          <input type="checkbox" checked={quick} onChange={(event) => setQuick(event.target.checked)} />
          Quick search
        </label>
      </div>
      <form className="admin-lookup" onSubmit={search}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={quick ? 'id, email reference, email or number' : 'id, name, email or number'}
        />
        <button type="submit" className="admin-btn admin-btn--primary" disabled={busy}>Look up</button>
      </form>
      {error ? <p className="admin-error">{error}</p> : null}
      {users.length === 0 && !busy ? (
        <p className="admin-empty">Search to open an account.</p>
      ) : (
        <div className="admin-split">
          <div className="admin-list">
            {users.map((user) => (
              <button
                type="button"
                key={user.userId}
                className={user.userId === selectedId ? 'admin-list-item is-active' : 'admin-list-item'}
                onClick={() => setSelectedId(user.userId)}
              >
                <strong>{user.name || 'Unknown'}</strong>
                <p className="admin-muted">{user.email || user.phoneNumber || user.loginCredential || userPhotoCountLabel(user)}</p>
              </button>
            ))}
          </div>
          {selected ? (
            <section className="admin-detail">
              <div className="admin-user">
                {photo ? <img src={photo.url} alt="" /> : <div className="admin-card__empty" style={{ width: 72, height: 72 }}>No photo</div>}
                <div>
                  <strong>
                    {selected.name || 'Unknown'}{selected.age != null ? ` | ${selected.age}` : ''}
                  </strong>
                  <p className="admin-muted">{selected.active === 'false' ? 'Disabled' : 'Active'}</p>
                  <p className="admin-muted">{userPhotoCountLabel(selected)}</p>
                </div>
              </div>
              <p className="admin-muted">{selected.email || 'No email'}</p>
              <p className="admin-muted">{selected.phoneNumber || selected.loginCredential || 'No number'}</p>
              <p className="admin-muted">
                {[selected.gender, selected.genderPreference].filter(Boolean).join(' → ') || 'No gender'}
              </p>
              {userLocationLines(selected).map((line) => (
                <p key={line} className="admin-muted">{line}</p>
              ))}
              <div className="admin-actions">
                {selected.active === 'false' ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    disabled={busy}
                    onClick={() => run('Enable this account?', () => enableUser(selected.userId))}
                  >
                    Enable account
                  </button>
                ) : (
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    disabled={busy}
                    onClick={() => run('Block this account?', () => adminReviewAction('block', [selected.userId]))}
                  >
                    Block account
                  </button>
                )}
                <button
                  type="button"
                  className="admin-btn"
                  disabled={busy}
                  onClick={() => run('Change this account to real location?', () => adminUpdateLocation([selected.userId]))}
                >
                  Change to real location
                </button>
                <button
                  type="button"
                  className="admin-btn"
                  disabled={busy}
                  onClick={() => run('Change gender for this account?', () => adminUpdateGender([selected.userId]))}
                >
                  Change gender
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn--danger"
                  disabled={busy}
                  onClick={() => run('Delete this account?', () => deleteAccount(selected.userId))}
                >
                  Delete account
                </button>
                <button type="button" className="admin-btn" onClick={() => props.onPreview(selected)}>
                  Preview
                </button>
                <button type="button" className="admin-btn" onClick={() => copyText(selected.userId)}>UserID</button>
                {selected.email ? <button type="button" className="admin-btn" onClick={() => copyText(selected.email)}>Copy email</button> : null}
                {selected.loginCredential ? <button type="button" className="admin-btn" onClick={() => copyText(selected.loginCredential)}>Copy credential</button> : null}
              </div>
            </section>
          ) : (
            <p className="admin-empty">Pick an account.</p>
          )}
        </div>
      )}
      {busy ? <p className="admin-muted">Working…</p> : null}
    </section>
  );
}

export default LookupPanel;
